import { Helmet } from "react-helmet-async";

const SITE_NAME = "Discount Fillers";
const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://discountfillers.com";

// Optimized default meta description — 154 chars, keyword-dense, action verb,
// USP-driven. Google typically truncates at 155 chars in search snippets.
const DEFAULT_DESC =
  "Buy wholesale Korean fillers, botox, biostimulators & skin boosters at Discount Fillers. Cold-chain shipping to clinics worldwide. MOQ 5 units.";

// Keywords: brand + core B2B intent + top product names + geo modifiers.
// Google largely ignores meta keywords, but Bing/Yandex still use them AND
// they act as free-text signal for internal search + AI crawlers.
const DEFAULT_KEYWORDS = [
  // Brand
  "Discount Fillers", "discountfillers.com",
  // Core B2B intents (high-value)
  "wholesale dermal fillers", "buy botulinum toxin online", "wholesale botox for clinics",
  "cheap Korean fillers", "aesthetic clinic supply", "medical injectables wholesale",
  "B2B aesthetic supplies", "clinic wholesale prices", "bulk buy dermal fillers",
  // Product-specific (highest search volume 2026)
  "Botulax wholesale", "Nabota wholesale", "Meditoxin bulk", "Innotox price",
  "Restylane wholesale", "Juvederm wholesale", "Belotero", "Neuramis", "Yvoire",
  "Sculptra wholesale", "PLLA biostimulator", "Rejuran", "Profhilo alternative",
  "Radiesse wholesale", "Aesthefill", "Chaeum Premium",
  // Category-level
  "wholesale HA filler", "Korean botox brands", "PDRN skin booster",
  "exosome therapy wholesale", "lipolysis injection bulk", "PLLA collagen stimulator",
  // Geo
  "wholesale fillers USA", "aesthetic supplies Europe", "buy fillers Mexico",
].join(", ");

const DEFAULT_OG_IMAGE =
  "https://images.pexels.com/photos/3997989/pexels-photo-3997989.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200&h=630&fit=crop";

/**
 * SEO meta tags + Open Graph + Twitter cards + canonical + Schema.org.
 * Pass `jsonLd` (object or array) to inject structured data.
 * Pass `noindex` for private/transactional pages.
 */
export const Seo = ({
  title,
  description = DEFAULT_DESC,
  keywords,
  path = "",
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogType = "website",
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} — Wholesale Aesthetic Supplies for Clinics`;
  const canonical = `${SITE_URL}${path}`;
  const kw = keywords ? `${keywords}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <meta name="googlebot" content={noindex ? "noindex" : "index, follow, max-snippet:-1, max-image-preview:large"} />
      <meta name="bingbot" content={noindex ? "noindex" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt || title || SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@discountfillers" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt || title || SITE_NAME} />

      {/* Extra hints */}
      <meta name="theme-color" content="#000000" />
      <meta name="format-detection" content="telephone=no" />

      {/* JSON-LD */}
      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

export const SITE = { name: SITE_NAME, url: SITE_URL };
